import { z } from 'zod'

import { MergeFieldWinnerSchema, RecordMergeType } from './merge'

// =====================
// Background matcher + confidence tiers (T4 — Appendix 1 "Matching: confidence
// tiers"). The matcher runs in the background (never a blocking modal) and
// classifies each candidate pair into a tier that dictates the action:
//
//   HARD   — exact hard key (same normalized email / verified external id):
//            auto-link silently (via the T3 reversible merge) and journal it.
//   MEDIUM — same normalized name + same company domain: PROPOSE a suggestion,
//            never auto-apply. "Under-merging is cheap; over-merging is data
//            corruption" — bias conservative.
//   LOW    — fuzzy name only: leave the records separate/provisional, no
//            suggestion. They get caught later when a harder signal appears
//            (or at the money-gate).
//
// Resolution surfaces opportunistically at the Deal money-gate ("new company,
// or your existing Acme account?") — never a dedicated admin page.
// =====================

export enum MatchTier {
  HARD = 'HARD',
  MEDIUM = 'MEDIUM',
  LOW = 'LOW',
}

export enum MatchSuggestionStatus {
  PENDING = 'PENDING',
  ACCEPTED = 'ACCEPTED',
  DISMISSED = 'DISMISSED',
}

// One proposed (or journaled) match between two records of the same spine type.
// Orientation is fixed at creation: accepting merges `sourceRecordId` (the
// would-be tombstone — typically the newer / provisional record) INTO
// `candidateRecordId` (the would-be survivor) via the T3 reversible merge.
export interface MatchSuggestion {
  id: string
  organizationId: string
  recordType: RecordMergeType
  /** The record that would become the tombstone if accepted. */
  sourceRecordId: string
  /** The record that would survive if accepted. */
  candidateRecordId: string
  tier: MatchTier
  /** Human-readable reasons, e.g. ["same normalized name", "same domain acme.com"]. */
  matchReasons: string[]
  status: MatchSuggestionStatus
  /** Set when the suggestion was accepted (or auto-linked) — the T3 journal row. */
  recordMergeId: string | null
  resolvedAt: string | Date | null
  /** Null for matcher auto-links (HARD tier journal rows). */
  resolvedById: string | null
  createdAt: string | Date
}

// Display payload attached to a suggestion so the UI can render names without
// N extra record fetches.
export interface MatchSuggestionRecordSummary {
  id: string
  label: string
  secondaryLabel?: string | null
}

export interface MatchSuggestionWithRecords extends MatchSuggestion {
  sourceRecord: MatchSuggestionRecordSummary | null
  candidateRecord: MatchSuggestionRecordSummary | null
}

export const AcceptMatchSuggestionSchema = z.object({
  // Resolutions for conflicting singular fields, passed through to the T3
  // merge, e.g. { email: 'survivor' }. Absence on a conflict triggers a
  // MERGE_SURVIVORSHIP_REQUIRED response carrying the conflicts.
  fieldWinners: z.record(z.string(), MergeFieldWinnerSchema).optional(),
})
export type AcceptMatchSuggestionRequest = z.infer<
  typeof AcceptMatchSuggestionSchema
>

// The Deal money-gate check (Appendix 1 "Who runs this with no data steward").
// Queried when a Deal is being created/attached to a contact: fuzzy identity is
// allowed to stay fuzzy until it touches money — this is where the system asks
// "new company, or your existing Acme account?".
export interface DealMoneyGateCheck {
  contactId: string | null
  entityId: string | null
  /** True when the contact has no linked entity but plausible existing entities exist. */
  entityAmbiguous: boolean
  /** Existing entities that plausibly ARE the contact's company (by name/domain). */
  candidateEntities: MatchSuggestionRecordSummary[]
  /** PENDING suggestions involving the contact/entity — worth resolving now. */
  pendingSuggestions: MatchSuggestionWithRecords[]
}
